/**
 * Shopify Cookie Consent Provider for headless storefronts.
 *
 * Loads Shopify's native privacy banner script (`storefront-banner.js`) and
 * initialises the banner via `window.privacyBanner.loadBanner()` (the current
 * API as of 2025-04). This replaces the legacy `window.Shopify.loadFeatures`
 * approach which is no longer present in the CDN script.
 *
 * `loadBanner` contacts Shopify servers (using the storefrontAccessToken) to
 * determine the visitor's region and render the banner when required.
 *
 * Prerequisites:
 *  - Enable the cookie banner in Shopify Admin → Settings → Customer Privacy → Cookie banner.
 *  - Set `VITE_CHECKOUT_DOMAIN`, `VITE_STOREFRONT_ROOT_DOMAIN`, and
 *    `VITE_STOREFRONT_API_PUBLIC_ACCESS_TOKEN` env vars.
 */

import { createContext, useContext, useEffect, useState } from "react";
import type { PropsWithChildren } from "react";

let bannerInitKey: string | null = null;
let bannerInitPromise: Promise<void> | null = null;
let bannerOptions: PrivacyBannerOptions | null = null;

// ---------------------------------------------------------------------------
// Global type declarations for Shopify's consent API injected by the banner
// script. These are NOT part of hydrogen-react.
// ---------------------------------------------------------------------------
interface ShopifyCustomerPrivacy {
  setTrackingConsent: (
    params: {
      analytics?: boolean;
      marketing?: boolean;
      preferences?: boolean;
      sale_of_data?: boolean;
      headlessStorefront?: boolean;
      checkoutRootDomain?: string;
      storefrontRootDomain?: string;
      storefrontAccessToken?: string;
    },
    callback: () => void,
  ) => void;
  currentVisitorConsent: () => VisitorConsentDetail;
  analyticsProcessingAllowed: () => boolean;
  marketingAllowed: () => boolean;
  preferencesProcessingAllowed: () => boolean;
  saleOfDataAllowed: () => boolean;
}

interface ShopifyGlobal {
  customerPrivacy?: ShopifyCustomerPrivacy;
}

interface PrivacyBannerOptions {
  storefrontAccessToken?: string;
  checkoutRootDomain?: string;
  storefrontRootDomain?: string;
  locale?: string;
  country?: string;
  showPreferences?: boolean;
}

interface PrivacyBannerGlobal {
  loadBanner: (options: PrivacyBannerOptions) => Promise<void>;
  showPreferences: (options?: PrivacyBannerOptions) => Promise<void>;
}

declare global {
  interface Window {
    Shopify?: ShopifyGlobal;
    privacyBanner?: PrivacyBannerGlobal;
  }
}

// Detail emitted by the `visitorConsentCollected` CustomEvent.
interface VisitorConsentDetail {
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
  preferencesAllowed: boolean;
  saleOfDataAllowed: boolean;
}

// ---------------------------------------------------------------------------
// Consent context
// ---------------------------------------------------------------------------
export interface ConsentState {
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
  preferencesAllowed: boolean;
  saleOfDataAllowed: boolean;
  /** True once the consent API has been loaded (regardless of user choice). */
  isReady: boolean;
}

const defaultConsentState: ConsentState = {
  analyticsAllowed: false,
  marketingAllowed: false,
  preferencesAllowed: false,
  saleOfDataAllowed: false,
  isReady: false,
};

const ConsentContext = createContext<ConsentState>(defaultConsentState);

/** Read the current consent state from anywhere in the tree. */
export function useConsent(): ConsentState {
  return useContext(ConsentContext);
}

/**
 * Opens Shopify's cookie preferences so visitors can change or withdraw
 * consent (AZOP asks for this link in every page footer). showPreferences
 * only opens with the same options loadBanner got.
 */
export function showCookiePreferences() {
  if (bannerOptions) void window.privacyBanner?.showPreferences(bannerOptions);
}

// ---------------------------------------------------------------------------
// Provider implementation
// ---------------------------------------------------------------------------
const BANNER_SCRIPT_URL =
  "https://cdn.shopify.com/shopifycloud/privacy-banner/storefront-banner.js";

function normalizeConsentDomain(
  domain: string | undefined,
): string | undefined {
  if (!domain) return undefined;

  const normalized = domain
    .replace(/^https?:\/\//, "")
    .replace(/:\d+$/, "")
    .replace(/\/$/, "");

  // Shopify rejects consent cookies for localhost-style domains.
  if (normalized === "localhost" || normalized === "127.0.0.1") {
    return undefined;
  }

  return normalized;
}

function toConsentState(
  detail: VisitorConsentDetail,
  isReady = true,
): ConsentState {
  return {
    analyticsAllowed: detail.analyticsAllowed,
    marketingAllowed: detail.marketingAllowed,
    preferencesAllowed: detail.preferencesAllowed,
    saleOfDataAllowed: detail.saleOfDataAllowed,
    isReady,
  };
}

const BANNER_CLOSE_LABELS: Record<string, string> = {
  HR: "Zatvori",
  DE: "Schließen",
};

/**
 * AZOP requires an X on the banner's first layer that closes it without
 * consent. Shopify's banner has none, so the X clicks Shopify's own Decline.
 * Returns false while the banner isn't in the DOM yet.
 */
function addBannerCloseButton(locale: string | undefined): boolean {
  const banner = document.getElementById("shopify-pc__banner");
  const decline = document.getElementById("shopify-pc__banner__btn-decline");
  if (!banner || !decline) return false;
  if (document.getElementById("shopify-pc__banner__btn-close")) return true;

  const button = document.createElement("button");
  button.id = "shopify-pc__banner__btn-close";
  button.type = "button";
  button.textContent = "×";
  button.setAttribute(
    "aria-label",
    BANNER_CLOSE_LABELS[locale?.toUpperCase() ?? ""] ?? "Close",
  );
  button.style.cssText =
    "position:absolute;top:8px;right:8px;padding:4px 8px;border:0;background:none;color:inherit;font-size:24px;line-height:1;cursor:pointer";
  button.addEventListener("click", () => decline.click());
  banner.appendChild(button);
  return true;
}

function readStoredConsent(): ConsentState | null {
  const stored = window.Shopify?.customerPrivacy?.currentVisitorConsent();

  if (!stored) return null;

  return toConsentState(stored);
}

interface ConsentProviderProps extends PropsWithChildren {
  checkoutDomain: string;
  storefrontRootDomain: string;
  storefrontAccessToken: string;
  /** BCP 47 / Shopify language code passed to loadBanner for banner translation (e.g. "HR", "EN"). */
  locale?: string;
  /** ISO country code passed to loadBanner for @inContext directive (e.g. "HR"). Required alongside locale for translated banner text. */
  country?: string;
}

export function ConsentProvider({
  children,
  checkoutDomain,
  storefrontRootDomain,
  storefrontAccessToken,
  locale,
  country,
}: ConsentProviderProps) {
  const [consent, setConsent] = useState<ConsentState>(defaultConsentState);
  const normalizedCheckoutDomain = normalizeConsentDomain(checkoutDomain);
  const normalizedStorefrontRootDomain =
    normalizeConsentDomain(storefrontRootDomain);
  const bannerConfigKey = JSON.stringify({
    checkoutDomain: normalizedCheckoutDomain,
    storefrontRootDomain: normalizedStorefrontRootDomain,
    storefrontAccessToken,
    locale,
    country,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    let bannerObserver: MutationObserver | undefined;

    // Listen for the banner's consent-collected event. Register early so we
    // don't miss it if loadBanner resolves synchronously.
    function handleConsentCollected(event: Event) {
      const detail = (event as CustomEvent<VisitorConsentDetail>).detail;
      setConsent(toConsentState(detail));
    }

    document.addEventListener(
      "visitorConsentCollected",
      handleConsentCollected,
    );

    async function initBanner() {
      // Avoid injecting the script twice (e.g. StrictMode double-mount).
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src="${BANNER_SCRIPT_URL}"]`,
      );

      if (!existingScript) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement("script");
          script.src = BANNER_SCRIPT_URL;
          script.async = true;
          script.addEventListener("load", () => resolve());
          script.addEventListener("error", () =>
            reject(new Error("[Consent] Failed to load Shopify banner script")),
          );
          document.head.appendChild(script);
        });
      } else if (!window.privacyBanner) {
        // Script tag exists but hasn't executed yet — wait for it.
        await new Promise<void>((resolve) => {
          existingScript.addEventListener("load", () => resolve(), {
            once: true,
          });
        });
      }

      if (!window.privacyBanner?.loadBanner) {
        console.error(
          "[Consent] window.privacyBanner.loadBanner not available",
        );
        setConsent((prev) => ({ ...prev, isReady: true }));
        return;
      }

      try {
        if (bannerInitKey !== bannerConfigKey) {
          bannerInitKey = bannerConfigKey;
          bannerOptions = {
            storefrontAccessToken,
            checkoutRootDomain: normalizedCheckoutDomain,
            storefrontRootDomain: normalizedStorefrontRootDomain,
            locale,
            country,
          };
          bannerInitPromise = window.privacyBanner.loadBanner(bannerOptions);
        }

        // `loadBanner` can re-render the Shopify banner. Keep it idempotent for
        // the current config so React remounts don't show the banner again.
        await bannerInitPromise;

        // The banner is added a few hundred ms after loadBanner resolves, and
        // not at all once the visitor has chosen. Shopify also fires
        // visitorConsentCollected on load, before the banner exists, so that
        // event can't end the wait.
        if (!addBannerCloseButton(locale)) {
          bannerObserver = new MutationObserver(() => {
            if (addBannerCloseButton(locale)) bannerObserver?.disconnect();
          });
          bannerObserver.observe(document.body, {
            childList: true,
            subtree: true,
          });
        }

        const stored = readStoredConsent();
        if (stored) {
          setConsent(stored);
          return;
        }

        setConsent((prev) => ({ ...prev, isReady: true }));
      } catch (error) {
        console.error("[Consent] Failed to initialise privacy banner:", error);
        bannerInitKey = null;
        bannerInitPromise = null;
        setConsent((prev) => ({ ...prev, isReady: true }));
      }
    }

    void initBanner();

    return () => {
      bannerObserver?.disconnect();
      document.removeEventListener(
        "visitorConsentCollected",
        handleConsentCollected,
      );
    };
  }, [
    bannerConfigKey,
    normalizedCheckoutDomain,
    normalizedStorefrontRootDomain,
    storefrontAccessToken,
    locale,
    country,
  ]);

  return (
    <ConsentContext.Provider value={consent}>
      {children}
    </ConsentContext.Provider>
  );
}
